import React,{useState,useEffect,useCallback,useRef} from 'react';
import axios from 'axios';
import {
  ShoppingCart,Plus,Minus,X,Search,Package,CheckCircle,ArrowLeft,
  Phone,MapPin,User,MessageCircle,ClipboardList,Loader,Send,
  ChevronRight,Star,Info,RefreshCw,Clock,Tag,Zap,Heart
} from 'lucide-react';
import {useLang} from '../i18n';

// ─── E-Menu UI translations ───────────────────────────────────
const EM={
  en:{
    searchPlaceholder:'Search products...',
    trackBtn:'Track',loginBtn:'Login',
    cartTitle:'Cart',yourDetails:'Your Details',orderPlaced:'Order Placed! 🎉',
    proceedBtn:'Proceed to Order',placeBtn:'Place Order',placing:'Placing...',
    fullName:'Full Name *',phone:'Phone Number',address:'Delivery Address',
    telegram:'Telegram (optional)',notes:'Order Notes (optional)',
    thankYou:'Thank you',received:'We received your order.',
    trackOrder:'Track My Order',search:'Search',
    outOfStock:'Out of Stock',
    loginTitle:'Login with Phone',registerTitle:'Create Account',
    myAccount:'My Account',signOut:'Sign Out',
    continueGuest:'Continue as guest',
    savedAddress:'SAVED ADDRESS',
    registerDesc:'Create an account so we can remember your details.',
    loginDesc:'Enter your phone number to load your saved details.',
    phoneRequired:'Please enter your phone number',
    nameRequired:'Please enter your name',
    loginFailed:'Login failed',registerFailed:'Registration failed',
    loggingIn:'Logging in...',creatingAccount:'Creating account...',
    savingChanges:'Saving...',saveChanges:'Save Changes',
    updateDetails:'Update My Details',
    dontHaveAccount:"Don't have an account? Register",
    alreadyHaveAccount:'Already have an account? Login',
    noteExpiry:'Your phone number is your account ID. Your details are saved securely and never expire.',
    telegramNotif:'Get Telegram Notifications',telegramDesc:'Real-time order status updates',
    telegramActive:'Telegram Updates Active',
    orderNumber:'ORDER NUMBER',items:'item',totalLabel:'Total',editCart:'Edit',
  },
  km:{
    searchPlaceholder:'ស្វែងរកផលិតផល...',
    trackBtn:'តាមដាន',loginBtn:'ចូល',
    cartTitle:'កន្ត្រក',yourDetails:'ព័ត៌មានរបស់អ្នក',orderPlaced:'បានបញ្ជាទិញ! 🎉',
    proceedBtn:'បន្តការបញ្ជាទិញ',placeBtn:'ដាក់ការបញ្ជាទិញ',placing:'កំពុងដំណើរការ...',
    fullName:'ឈ្មោះពេញ *',phone:'លេខទូរស័ព្ទ',address:'អាសយដ្ឋានដឹកជញ្ជូន',
    telegram:'Telegram (ស្រេចចិត្ត)',notes:'កំណត់សម្គាល់ (ស្រេចចិត្ត)',
    thankYou:'អរគុណ',received:'យើងបានទទួលការបញ្ជាទិញរបស់អ្នក។',
    trackOrder:'តាមដានការបញ្ជាទិញ',search:'ស្វែងរក',
    outOfStock:'អស់ស្តុក',
    loginTitle:'ចូលដោយលេខទូរស័ព្ទ',registerTitle:'បង្កើតគណនី',
    myAccount:'គណនីរបស់ខ្ញុំ',signOut:'ចាកចេញ',
    continueGuest:'បន្តដោយមិនចូល',
    savedAddress:'អាសយដ្ឋានដែលបានរក្សាទុក',
    registerDesc:'បង្កើតគណនីដើម្បីឱ្យយើងចងចាំព័ត៌មានរបស់អ្នក។',
    loginDesc:'បញ្ចូលលេខទូរស័ព្ទរបស់អ្នក ដើម្បីផ្ទុកព័ត៌មានដែលបានរក្សាទុក។',
    phoneRequired:'សូមបញ្ចូលលេខទូរស័ព្ទ',
    nameRequired:'សូមបញ្ចូលឈ្មោះ',
    loginFailed:'ការចូលបានបរាជ័យ',registerFailed:'ការចុះឈ្មោះបានបរាជ័យ',
    loggingIn:'កំពុងចូល...',creatingAccount:'កំពុងបង្កើតគណនី...',
    savingChanges:'កំពុងរក្សាទុក...',saveChanges:'រក្សាទុក',
    updateDetails:'កែប្រែព័ត៌មាន',
    dontHaveAccount:'មិនមានគណនី? ចុះឈ្មោះ',
    alreadyHaveAccount:'មានគណនីហើយ? ចូល',
    noteExpiry:'លេខទូរស័ព្ទគឺជា ID គណនីរបស់អ្នក។ ព័ត៌មានត្រូវបានរក្សាទុកដោយសុវត្ថិភាព។',
    telegramNotif:'ទទួលការជូនដំណឹង Telegram',telegramDesc:'ការធ្វើបច្ចុប្បន្នភាពស្ថានភាពការបញ្ជាទិញ',
    telegramActive:'Telegram កំពុងដំណើរការ',
    orderNumber:'លេខការបញ្ជាទិញ',items:'ចំណុច',totalLabel:'សរុប',editCart:'កែ',
  },
};

const fmt=n=>`$${parseFloat(n||0).toFixed(2)}`;
const API =
  window.location.hostname === 'localhost'
    ? 'http://localhost:5000'
    : '';  // relative URL in production — same origin, no CORS
console.log("API",API);
const STATUS_MAP={
  draft:    {label:'Received',  color:'#64748b',bg:'#f1f5f9',icon:'📥'},
  pending:  {label:'Pending',   color:'#d97706',bg:'#fef3c7',icon:'⏳'},
  confirmed:{label:'Confirmed', color:'#2563eb',bg:'#dbeafe',icon:'✅'},
  processing:{label:'Preparing',color:'#7c3aed',bg:'#ede9fe',icon:'👨‍🍳'},
  shipped:  {label:'On the Way',color:'#059669',bg:'#d1fae5',icon:'🚚'},
  completed:{label:'Completed', color:'#16a34a',bg:'#dcfce7',icon:'🎉'},
  cancelled:{label:'Cancelled', color:'#dc2626',bg:'#fee2e2',icon:'❌'},
};

/* ─── useIsMobile ─── */
function useIsMobile(){
  const[m,setM]=useState(()=>window.innerWidth<768);
  useEffect(()=>{const h=()=>setM(window.innerWidth<768);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h)},[]);
  return m;
}

/* ─── Image with fallback ─── */
function Img({src,alt,style}){
  const[err,setErr]=useState(false);
  const full=src&&!src.startsWith('http')?`${API}${src}`:src;
  if(!src||err)return <div style={{...style,background:'#f1f5f9',display:'flex',alignItems:'center',justifyContent:'center'}}><Package size={Math.min(parseInt(style?.width||40)/2.5,28)} color="#cbd5e1"/></div>;
  return <img src={full} alt={alt||''} style={style} onError={()=>setErr(true)}/>;
}

/* ─── Product Card ─── */
function ProductCard({p,cartItem,onAdd,onDec,mob,lang}){
  const displayName = (lang==='km' && p.name_kh) ? p.name_kh : (p.name||'');
  const[selUom,setSelUom]=useState(null);
  const uomList=(p.uoms||[]).filter(u=>parseFloat(u.price)>0);
  const price=selUom?parseFloat(uomList.find(u=>u.uom_id===selUom)?.price||p.sale_price):parseFloat(p.sale_price);
  const uomLabel=selUom?uomList.find(u=>u.uom_id===selUom)?.uom?.name:p.uom?.name||'Unit';
  // inStock: allow_negative_stock = always orderable; null = unlimited (service/non-tracked); stock > 0 = has qty
  const inStock = p.allow_negative_stock || p.stock === null || p.stock > 0;
  const lowStock=p.stock!==null&&p.stock>0&&p.stock<=5;
  const qtyInCart=cartItem?.reduce((s,i)=>s+i.qty,0)||0;

  return(
    <div style={{
      background:'#fff',borderRadius:5,overflow:'hidden',position:'relative',
      boxShadow:qtyInCart>0?'0 0 0 2.5px #6366f1,0 8px 30px rgba(99,102,241,.18)':'0 2px 16px rgba(0,0,0,.06)',
      transition:'box-shadow .2s,transform .15s',
      transform:qtyInCart>0?'translateY(-2px)':'none',
      opacity:inStock?1:.55,
    }}>
      {/* qty badge */}
      {qtyInCart>0&&<div style={{position:'absolute',top:8,left:8,zIndex:4,
        width:26,height:26,borderRadius:'50%',background:'#6366f1',
        color:'#fff',fontSize:12,fontWeight:900,display:'flex',alignItems:'center',justifyContent:'center',
        boxShadow:'0 3px 10px rgba(99,102,241,.5)'}}>
        {qtyInCart}
      </div>}
      {/* stock badge */}
      {p.stock!==null&&<div style={{position:'absolute',top:8,right:8,zIndex:4,
        padding:'2px 8px',borderRadius:20,fontSize:10,fontWeight:700,
        background:p.stock<=0?'#ef4444':lowStock?'#f59e0b':'#10b981',color:'#fff',
        boxShadow:'0 2px 6px rgba(0,0,0,.15)'}}>
        {p.stock<=0?'OUT':lowStock?`${p.stock} left`:p.stock}
      </div>}
      {/* Featured badge */}
      {p.emenu_featured&&<div style={{position:'absolute',top:qtyInCart>0?38:8,left:8,zIndex:4,
        display:'flex',alignItems:'center',gap:3,padding:'2px 7px',borderRadius:20,
        fontSize:9,fontWeight:800,background:'#f59e0b',color:'#fff',
        boxShadow:'0 2px 6px rgba(245,158,11,.5)',letterSpacing:.3}}>
        <Zap size={9}/> HOT
      </div>}
      {/* Popularity / rating badge */}
      {p.emenu_popularity!=null&&p.emenu_popularity>0&&<div style={{
        position:'absolute',bottom:8,right:8,zIndex:4,
        display:'flex',alignItems:'center',gap:3,padding:'2px 7px',borderRadius:20,
        fontSize:9,fontWeight:800,
        background:p.emenu_popularity>=80?'#ef4444':p.emenu_popularity>=60?'#f59e0b':'#6366f1',
        color:'#fff',boxShadow:'0 2px 6px rgba(0,0,0,.2)',letterSpacing:.2}}>
        <Star size={8} style={{fill:'#fff'}}/> {p.emenu_popularity}
      </div>}

      {/* image */}
      <div style={{height:mob?160:160,overflow:'hidden',position:'relative'}}>
        <Img src={p.image_url} alt={displayName} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
        {!inStock&&<div style={{position:'absolute',inset:0,background:'rgba(0,0,0,.35)',display:'flex',alignItems:'center',justifyContent:'center'}}>
          <span style={{color:'#fff',fontSize:13,fontWeight:800,background:'rgba(239,68,68,.85)',padding:'4px 10px',borderRadius:8}}>Out of Stock</span>
        </div>}
      </div>

      <div style={{padding:mob?'10px 10px 12px':'12px 14px 14px'}}>
        <div style={{fontSize:mob?11:13,fontWeight:700,color:'#1e293b',lineHeight:1.35,marginBottom:3,
          overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{displayName}</div>
        {p.category&&<div style={{fontSize:10,color:'#94a3b8',marginBottom:6,display:'flex',alignItems:'center',gap:3}}>
          <Tag size={9}/>{p.category.name}
        </div>}

        {/* UoM selector */}
        {uomList.length>0&&(
          <select value={selUom??''} onChange={e=>setSelUom(e.target.value?parseInt(e.target.value):null)}
            onClick={e=>e.stopPropagation()}
            style={{width:'100%',fontSize:11,padding:'5px 7px',borderRadius:8,border:'1px solid #e2e8f0',
              background:'#f8fafc',color:'#475569',marginBottom:7,outline:'none'}}>
            <option value="">{p.uom?.name||'Unit'} — {fmt(p.sale_price)}</option>
            {uomList.map(u=><option key={u.uom_id} value={u.uom_id}>{u.uom?.name} — {fmt(u.price)}</option>)}
          </select>
        )}

        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:6}}>
          <div>
            <div style={{fontSize:mob?15:17,fontWeight:900,color:'#6366f1',lineHeight:1}}>{fmt(price)}</div>
            <div style={{fontSize:9,color:'#94a3b8',marginTop:1}}>{uomLabel}</div>
          </div>

          {/* +/- controls */}
          {qtyInCart>0?(
            <div style={{display:'flex',alignItems:'center',gap:5}}>
              <button onClick={()=>onDec(p.id,selUom)} style={{width:26,height:26,borderRadius:'50%',border:'none',background:'#544ee8',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',flexShrink:0}}>
                <Minus size={12}/>
              </button>
              <span style={{fontSize:13,fontWeight:800,color:'#544ee8',minWidth:16,textAlign:'center'}}>{qtyInCart}</span>
              <button onClick={()=>inStock&&onAdd(p,selUom,price)} style={{width:26,height:26,borderRadius:'50%',background:'#6366f1',border:'none',cursor:inStock?'pointer':'not-allowed',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',flexShrink:0}}>
                <Plus size={12}/>
              </button>
            </div>
          ):(
            <button onClick={()=>inStock&&onAdd(p,selUom,price)} disabled={!inStock}
              style={{width:32,height:32,borderRadius:'50%',border:'none',cursor:inStock?'pointer':'not-allowed',
                background:inStock?'#6366f1':'#e2e8f0',color:'#fff',
                display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,
                boxShadow:inStock?'0 4px 14px rgba(99,102,241,.4)':'none',transition:'all .15s'}}>
              <Plus size={15}/>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Cart Sheet (bottom drawer) ─── */
function CartSheet({cart,branch,onClose,onQty,onRemove,onSubmit,submitting,customer,lang}){
  const m=EM[lang]||EM.en;
  const[step,setStep]=useState('cart');
  const[info,setInfo]=useState({
    name:  customer?.name    || '',
    phone: customer?.phone   || '',
    address: customer?.address || '',
    telegram:'',notes:''
  });
  const[done,setDone]=useState(null);
  const mob=useIsMobile();

  // Re-fill when customer changes (e.g. login while cart is open)
  useEffect(()=>{
    if(customer){
      setInfo(f=>({
        ...f,
        name:  customer.name    || f.name,
        phone: customer.phone   || f.phone,
        address: customer.address || f.address,
      }));
    }
  },[customer]);

  const total=cart.reduce((s,i)=>s+i.price*i.qty,0);
  const totalQty=cart.reduce((s,i)=>s+i.qty,0);
  const F=(k,v)=>setInfo(f=>({...f,[k]:v}));

  const place=async()=>{
    if(!info.name.trim()){alert(m.nameRequired);return}
    const r=await onSubmit(info);
    if(r){setDone(r);setStep('done')}
  };

  /* step indicator */
  const steps=['cart','info','done'];
  const stepIdx=steps.indexOf(step);

  return(
    <div style={{position:'fixed',inset:0,zIndex:200,display:'flex',flexDirection:'column'}}>
      <div onClick={()=>step!=='done'&&onClose()} style={{position:'absolute',inset:0,background:'rgba(15,23,42,.6)',backdropFilter:'blur(4px)'}}/>
      <div style={{
        position:'absolute',bottom:0,left:0,right:0,
        background:'#fff',borderRadius:'24px 24px 0 0',
        maxHeight:mob?'94vh':'88vh',
        display:'flex',flexDirection:'column',
        boxShadow:'0 -24px 80px rgba(0,0,0,.25)',
      }}>
        {/* drag handle */}
        <div style={{padding:'10px 0 0',display:'flex',justifyContent:'center'}}>
          <div style={{width:36,height:4,borderRadius:2,background:'#e2e8f0'}}/>
        </div>

        {/* header */}
        <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 20px 12px',borderBottom:'1px solid #f1f5f9'}}>
          {step==='info'&&<button onClick={()=>setStep('cart')} style={btnCircle}><ArrowLeft size={16}/></button>}
          <div style={{flex:1}}>
            <div style={{fontWeight:800,fontSize:16,color:'#0f172a'}}>
              {step==='cart'&&<><ShoppingCart size={16} style={{verticalAlign:'middle',marginRight:6,color:'#6366f1'}}/>{m.cartTitle} · {totalQty} {lang==='en'&&totalQty!==1?m.items+'s':m.items}</>}
              {step==='info'&&<><User size={15} style={{verticalAlign:'middle',marginRight:6,color:'#6366f1'}}/>{m.yourDetails}</>}
              {step==='done'&&<>{m.orderPlaced}</>}
            </div>
            {step==='cart'&&<div style={{fontSize:11,color:'#94a3b8',marginTop:1}}>{branch?.name}</div>}
          </div>
          {step!=='done'&&<button onClick={onClose} style={btnCircle}><X size={16}/></button>}
        </div>

        {/* ── CART ── */}
        {step==='cart'&&<>
          <div style={{flex:1,overflowY:'auto',padding:'8px 16px 4px'}}>
            {cart.map((item,i)=>(
              <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 0',borderBottom:'1px solid #f8fafc'}}>
                <div style={{width:52,height:52,borderRadius:12,overflow:'hidden',flexShrink:0}}>
                  <Img src={item.image_url} alt={item.name} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:'#1e293b',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.name}</div>
                  {item.uomName&&<div style={{fontSize:10,color:'#94a3b8'}}>{item.uomName}</div>}
                  <div style={{fontSize:13,fontWeight:800,color:'#6366f1',marginTop:2}}>{fmt(item.price)} × {item.qty} = <span style={{color:'#0f172a'}}>{fmt(item.price*item.qty)}</span></div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
                  <button onClick={()=>onQty(i,item.qty-1)} style={{
                    ...btnSmall,
                    background:item.qty===1?'#fee2e2':'#544ee8',
                    borderColor:item.qty===1?'#fca5a5':'#544ee8',
                    color:'#fff',
                    border:'none',
                  }}>
                    {item.qty===1?<X size={11}/>:<Minus size={11}/>}
                  </button>
                  <span style={{fontSize:13,fontWeight:800,minWidth:18,textAlign:'center',color:'#544ee8'}}>{item.qty}</span>
                  <button onClick={()=>onQty(i,item.qty+1)} style={{...btnSmall,background:'#6366f1',border:'none',color:'#fff'}}><Plus size={11}/></button>
                </div>
              </div>
            ))}
          </div>
          <div style={{padding:'12px 16px',borderTop:'1px solid #f1f5f9'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
              <span style={{fontSize:14,color:'#64748b',fontWeight:600}}>{m.totalLabel}</span>
              <span style={{fontSize:24,fontWeight:900,color:'#6366f1'}}>{fmt(total)}</span>
            </div>
            <button onClick={()=>setStep('info')} style={btnPrimary}>
              {m.proceedBtn} <ChevronRight size={18}/>
            </button>
          </div>
        </>}

        {/* ── INFO FORM ── */}
        {step==='info'&&<>
          <div style={{flex:1,overflowY:'auto',padding:'12px 20px 4px'}}>

            {/* Logged-in banner */}
            {customer&&(
              <div style={{background:'#eef2ff',borderRadius:12,padding:'10px 14px',marginBottom:14,
                display:'flex',alignItems:'center',gap:8,border:'1px solid #c7d2fe'}}>
                <div style={{width:30,height:30,borderRadius:'50%',background:'#6366f1',
                  display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <User size={14} color="#fff"/>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:800,color:'#4338ca',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{customer.name}</div>
                  <div style={{fontSize:11,color:'#6366f1'}}>{customer.phone}</div>
                </div>
                <div style={{fontSize:10,color:'#6366f1',background:'#e0e7ff',padding:'2px 8px',borderRadius:20,fontWeight:700,flexShrink:0}}>Saved ✓</div>
              </div>
            )}

            {/* Name */}
            <Field icon={<User size={13}/>} label={m.fullName}>
              <input value={info.name} onChange={e=>F('name',e.target.value)} placeholder="Enter your name" style={inputStyle}/>
            </Field>

            {/* Phone */}
            <Field icon={<Phone size={13}/>} label={m.phone}>
              <input value={info.phone} onChange={e=>F('phone',e.target.value)} placeholder="+855 12 345 678" type="tel" style={inputStyle}/>
            </Field>

            {/* Address */}
            <Field icon={<MapPin size={13}/>} label={m.address}>
              <textarea value={info.address} onChange={e=>F('address',e.target.value)}
                placeholder="Street, district, city..." rows={2}
                style={{...inputStyle,resize:'none',lineHeight:1.5}}/>
            </Field>

            {/* Telegram CTA */}
            <div style={{marginBottom:14,padding:'14px 16px',
              background:'linear-gradient(135deg,#eff6ff,#eef2ff)',
              borderRadius:14,border:'1px solid #bfdbfe'}}>
              <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:8}}>
                <div style={{width:28,height:28,borderRadius:8,background:'#2563eb',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <MessageCircle size={15} color="#fff"/>
                </div>
                <div>
                  <div style={{fontSize:12,fontWeight:800,color:'#1e40af'}}>{m.telegramNotif}</div>
                  <div style={{fontSize:10,color:'#3b82f6'}}>{m.telegramDesc}</div>
                </div>
              </div>
              <input value={info.telegram} onChange={e=>F('telegram',e.target.value)}
                placeholder="@YourUsername (optional)"
                style={{...inputStyle,border:'1.5px solid #93c5fd',marginBottom:0}}/>
              <div style={{fontSize:10,color:'#2563eb',marginTop:6,display:'flex',alignItems:'center',gap:4}}>
                <Zap size={9}/>We'll notify you when your order status changes
              </div>
            </div>

            {/* Notes */}
            <Field icon={<ClipboardList size={13}/>} label={m.notes}>
              <textarea value={info.notes} onChange={e=>F('notes',e.target.value)}
                placeholder="Special instructions, allergies..." rows={2}
                style={{...inputStyle,resize:'none',lineHeight:1.5}}/>
            </Field>
          </div>

          {/* mini summary + submit */}
          <div style={{padding:'10px 20px 14px',background:'#f8fafc',borderTop:'1px solid #f1f5f9'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
              <span style={{fontSize:12,color:'#64748b'}}>{totalQty} {m.items} · {fmt(total)}</span>
              <button onClick={()=>setStep('cart')} style={{fontSize:12,color:'#6366f1',background:'none',border:'none',cursor:'pointer',fontWeight:700}}>{m.editCart}</button>
            </div>
            <button onClick={place} disabled={submitting||!info.name.trim()}
              style={{...btnPrimary,opacity:(submitting||!info.name.trim())?0.6:1,cursor:submitting?'wait':'pointer'}}>
              {submitting?<><Loader size={16} style={{animation:'spin 1s linear infinite'}}/> {m.placing}</>:<><Send size={16}/> {m.placeBtn}</>}
            </button>
          </div>
        </>}

        {/* ── DONE ── */}
        {step==='done'&&done&&<div style={{flex:1,overflowY:'auto',padding:'20px 24px'}}>
          {/* Big success */}
          <div style={{textAlign:'center',marginBottom:24}}>
            <div style={{width:80,height:80,borderRadius:'50%',margin:'0 auto 16px',
              background:'linear-gradient(135deg,#d1fae5,#a7f3d0)',
              display:'flex',alignItems:'center',justifyContent:'center',fontSize:38}}>🎉</div>
            <div style={{fontSize:22,fontWeight:900,color:'#0f172a',marginBottom:6}}>{m.orderPlaced}</div>
            <div style={{fontSize:14,color:'#64748b'}}>{m.thankYou}, <b>{done.customer_name}</b>! {m.received}</div>
          </div>

          {/* Order card */}
          <div style={{background:'linear-gradient(135deg,#6366f1,#4f46e5)',borderRadius:16,padding:'18px 20px',marginBottom:16,color:'#fff'}}>
            <div style={{fontSize:10,opacity:.75,letterSpacing:.8,marginBottom:4}}>{m.orderNumber}</div>
            <div style={{fontSize:26,fontWeight:900,fontFamily:'monospace',letterSpacing:1,marginBottom:12}}>{done.order_number}</div>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,opacity:.9}}>
              <span>{done.items} {lang==='en'&&done.items!==1?m.items+'s':m.items}</span>
              <span style={{fontWeight:800,fontSize:16}}>{fmt(done.total_amount)}</span>
            </div>
          </div>

          {/* Telegram notice if given */}
          {info.telegram&&<div style={{background:'linear-gradient(135deg,#eff6ff,#eef2ff)',borderRadius:14,padding:'14px 16px',marginBottom:14,border:'1px solid #bfdbfe'}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
              <MessageCircle size={15} color="#2563eb"/>
              <span style={{fontSize:13,fontWeight:700,color:'#1e40af'}}>{m.telegramActive}</span>
            </div>
            <div style={{fontSize:12,color:'#3b82f6'}}>
              We'll notify <b>@{info.telegram.replace('@','')}</b> when your order status changes.
            </div>
          </div>}

          {/* Save order number reminder */}
          <div style={{background:'#fefce8',borderRadius:12,padding:'12px 16px',marginBottom:20,border:'1px solid #fde68a'}}>
            <div style={{fontSize:11,color:'#92400e',fontWeight:700,marginBottom:4}}>📋 Save your order number to track it</div>
            <div style={{fontFamily:'monospace',fontSize:17,fontWeight:900,color:'#92400e'}}>{done.order_number}</div>
          </div>

          <button onClick={onClose} style={btnPrimary}>Back to Menu</button>
        </div>}
      </div>
    </div>
  );
}

/* ─── Track Order Drawer ─── */
function TrackDrawer({onClose,lang}){
  const m=EM[lang]||EM.en;
  const[num,setNum]=useState('');
  const[order,setOrder]=useState(null);
  const[loading,setLoading]=useState(false);
  const[err,setErr]=useState('');

  const track=async()=>{
    const n=num.trim().toUpperCase();
    if(!n)return;
    setLoading(true);setErr('');setOrder(null);
    try{
      const r=await axios.get(`${API}/api/public/menu/_/order/${n}`);
      setOrder(r.data);
    }catch(e){setErr(e.response?.data?.error||'Order not found')}
    finally{setLoading(false)}
  };

  const s=order?STATUS_MAP[order.status]||STATUS_MAP.pending:null;

  return(
    <div style={{position:'fixed',inset:0,zIndex:200,display:'flex',flexDirection:'column'}}>
      <div onClick={onClose} style={{position:'absolute',inset:0,background:'rgba(15,23,42,.6)',backdropFilter:'blur(4px)'}}/>
      <div style={{position:'absolute',bottom:0,left:0,right:0,background:'#fff',
        borderRadius:'24px 24px 0 0',maxHeight:'85vh',display:'flex',flexDirection:'column',
        boxShadow:'0 -24px 80px rgba(0,0,0,.25)'}}>
        <div style={{padding:'10px 0 0',display:'flex',justifyContent:'center'}}>
          <div style={{width:36,height:4,borderRadius:2,background:'#e2e8f0'}}/>
        </div>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 20px 12px',borderBottom:'1px solid #f1f5f9'}}>
          <div style={{fontWeight:800,fontSize:16,color:'#0f172a',display:'flex',alignItems:'center',gap:7}}>
            <ClipboardList size={17} color="#6366f1"/>{m.trackOrder}
          </div>
          <button onClick={onClose} style={btnCircle}><X size={16}/></button>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:'16px 20px'}}>
          <div style={{display:'flex',gap:8,marginBottom:16}}>
            <input value={num} onChange={e=>setNum(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&track()}
              placeholder="e.g. ORD-00001"
              style={{...inputStyle,flex:1,marginBottom:0,fontFamily:'monospace',letterSpacing:.5}}/>
            <button onClick={track} disabled={loading}
              style={{padding:'12px 18px',background:'#6366f1',color:'#fff',border:'none',borderRadius:12,cursor:'pointer',fontWeight:700,display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
              {loading?<Loader size={16} style={{animation:'spin 1s linear infinite'}}/>:<Search size={16}/>}
            </button>
          </div>
          {err&&<div style={{background:'#fee2e2',color:'#dc2626',padding:'11px 14px',borderRadius:12,fontSize:13,marginBottom:14,fontWeight:600}}>{err}</div>}
          {order&&s&&<>
            <div style={{background:s.bg,borderRadius:16,padding:'16px 20px',marginBottom:14,display:'flex',alignItems:'center',gap:14}}>
              <span style={{fontSize:36}}>{s.icon}</span>
              <div>
                <div style={{fontSize:11,color:s.color,fontWeight:700,letterSpacing:.5}}>STATUS</div>
                <div style={{fontSize:20,fontWeight:900,color:s.color}}>{s.label}</div>
              </div>
            </div>
            <div style={{background:'#f8fafc',borderRadius:14,padding:'14px 16px',marginBottom:12}}>
              <Row label="Order #" val={<span style={{fontFamily:'monospace',color:'#6366f1',fontWeight:700}}>{order.order_number}</span>}/>
              <Row label="Branch" val={order.branch?.name}/>
              <Row label="Customer" val={order.customer?.name}/>
              <Row label="Total" val={<span style={{fontWeight:900,color:'#6366f1',fontSize:15}}>{fmt(order.total_amount)}</span>}/>
            </div>
            <div style={{background:'#fff',border:'1px solid #f1f5f9',borderRadius:12,overflow:'hidden'}}>
              {(order.items||[]).map((item,i)=>(
                <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',borderBottom:i<order.items.length-1?'1px solid #f1f5f9':'none'}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:'#1e293b'}}>{item.product?.name}</div>
                    <div style={{fontSize:11,color:'#94a3b8'}}>× {item.quantity}</div>
                  </div>
                  <div style={{fontWeight:800,color:'#6366f1'}}>{fmt(item.line_total)}</div>
                </div>
              ))}
            </div>
          </>}
        </div>
      </div>
    </div>
  );
}

/* ─── tiny helpers ─── */
function Field({icon,label,children}){
  return(
    <div style={{marginBottom:14}}>
      <label style={{fontSize:12,fontWeight:700,color:'#475569',display:'flex',alignItems:'center',gap:5,marginBottom:6}}>
        {icon}{label}
      </label>
      {children}
    </div>
  );
}
function Row({label,val}){
  return(
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8,fontSize:13}}>
      <span style={{color:'#64748b'}}>{label}</span>
      <span style={{fontWeight:600,color:'#0f172a'}}>{val}</span>
    </div>
  );
}
const inputStyle={width:'100%',padding:'12px 14px',borderRadius:12,border:'1.5px solid #e2e8f0',
  fontSize:14,outline:'none',boxSizing:'border-box',background:'#f8fafc',color:'#0f172a',
  fontFamily:'inherit'};
const btnPrimary={width:'100%',padding:'15px',background:'linear-gradient(135deg,#6366f1,#4f46e5)',
  color:'#fff',border:'none',borderRadius:14,fontSize:15,fontWeight:800,cursor:'pointer',
  display:'flex',alignItems:'center',justifyContent:'center',gap:8,letterSpacing:.2};
const btnCircle={width:34,height:34,borderRadius:'50%',border:'none',background:'#f1f5f9',
  cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#475569',flexShrink:0};
const btnSmall={width:26,height:26,borderRadius:'50%',border:'1.5px solid #e2e8f0',background:'#fff',
  cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#475569'};

// ─── Customer key for localStorage (per branch) ─────────────────
const custKey = branchCode => `emenu_customer_${branchCode}`;
const loadSavedCustomer = branchCode => {
  try { const s = localStorage.getItem(custKey(branchCode)); return s ? JSON.parse(s) : null; }
  catch { return null; }
};
const saveCustomer = (branchCode, cust) => {
  try { localStorage.setItem(custKey(branchCode), JSON.stringify(cust)); } catch {}
};
const clearCustomer = branchCode => {
  try { localStorage.removeItem(custKey(branchCode)); } catch {}
};

/* ─── Customer Auth Modal ─── */
function CustomerAuthModal({ branchCode, onClose, onSuccess, customer, onLogout, lang }) {
  const m=EM[lang]||EM.en;
  const [mode, setMode]       = useState(customer ? 'profile' : 'choose');
  const [phone, setPhone]     = useState('');
  const [name, setName]       = useState('');
  const [address, setAddress] = useState('');
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState('');

  // When customer changes externally, update mode
  useEffect(()=>{ setMode(customer ? 'profile' : 'choose'); },[customer]);

  const login = async () => {
    if (!phone.trim()) { setErr(m.phoneRequired); return; }
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/api/public/menu/${branchCode}/customer/login`, { phone: phone.trim() });
      saveCustomer(branchCode, r.data);
      onSuccess(r.data);
    } catch (e) { setErr(e.response?.data?.error || m.loginFailed); }
    finally { setBusy(false); }
  };

  const register = async () => {
    const finalName    = name.trim()    || customer?.name    || '';
    const finalPhone   = phone.trim()   || customer?.phone   || '';
    const finalAddress = address.trim() || customer?.address || '';
    if (!finalName)  { setErr(m.nameRequired); return; }
    if (!finalPhone) { setErr(m.phoneRequired); return; }
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/api/public/menu/${branchCode}/customer/register`, {
        name: finalName, phone: finalPhone, address: finalAddress || null,
      });
      saveCustomer(branchCode, r.data);
      onSuccess(r.data);
    } catch (e) { setErr(e.response?.data?.error || m.registerFailed); }
    finally { setBusy(false); }
  };

  const accentStyle = { background: 'linear-gradient(135deg,#6366f1,#4f46e5)', color: '#fff', border: 'none',
    borderRadius: 14, fontSize: 15, fontWeight: 800, cursor: 'pointer', padding: '14px',
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 };

  return (
    <div style={{position:'fixed',inset:0,zIndex:300,display:'flex',flexDirection:'column'}}>
      <div onClick={onClose} style={{position:'absolute',inset:0,background:'rgba(15,23,42,.6)',backdropFilter:'blur(4px)'}}/>
      <div style={{position:'absolute',bottom:0,left:0,right:0,background:'#fff',
        borderRadius:'24px 24px 0 0',maxHeight:'90vh',display:'flex',flexDirection:'column',
        boxShadow:'0 -24px 80px rgba(0,0,0,.25)'}}>
        <div style={{padding:'10px 0 0',display:'flex',justifyContent:'center'}}>
          <div style={{width:36,height:4,borderRadius:2,background:'#e2e8f0'}}/>
        </div>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 20px 12px',borderBottom:'1px solid #f1f5f9'}}>
          <div style={{fontWeight:800,fontSize:16,color:'#0f172a',display:'flex',alignItems:'center',gap:7}}>
            {mode==='profile'  &&<><User size={17} color="#6366f1"/>{m.myAccount}</>}
            {mode==='choose'   &&<><User size={17} color="#6366f1"/>{m.myAccount}</>}
            {mode==='login'    &&<><Phone size={17} color="#6366f1"/>{m.loginTitle}</>}
            {mode==='register' &&<><User size={17} color="#6366f1"/>{customer?m.updateDetails:m.registerTitle}</>}
          </div>
          <button onClick={onClose} style={btnCircle}><X size={16}/></button>
        </div>

        <div style={{flex:1,overflowY:'auto',padding:'20px'}}>
          {err && <div style={{background:'#fee2e2',color:'#dc2626',padding:'11px 14px',borderRadius:12,fontSize:13,marginBottom:14,fontWeight:600}}>{err}</div>}

          {/* ── PROFILE (logged in) ── */}
          {mode==='profile'&&customer&&(
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div style={{textAlign:'center',padding:'10px 0 20px'}}>
                <div style={{width:80,height:80,borderRadius:'50%',
                  background:'linear-gradient(135deg,#6366f1,#4f46e5)',
                  display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px',
                  boxShadow:'0 8px 24px rgba(99,102,241,.35)'}}>
                  <User size={36} color="#fff"/>
                </div>
                <div style={{fontWeight:900,fontSize:20,color:'#0f172a',marginBottom:4}}>{customer.name}</div>
                <div style={{fontSize:13,color:'#6366f1',fontWeight:600,display:'flex',alignItems:'center',justifyContent:'center',gap:5}}>
                  <Phone size={13}/>{customer.phone}
                </div>
              </div>
              {customer.address&&(
                <div style={{background:'#f8fafc',borderRadius:12,padding:'12px 14px',border:'1px solid #f1f5f9'}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#94a3b8',marginBottom:4,display:'flex',alignItems:'center',gap:4}}><MapPin size={11}/>SAVED ADDRESS</div>
                  <div style={{fontSize:13,color:'#334155',lineHeight:1.5}}>{customer.address}</div>
                </div>
              )}
              <div style={{background:'#f0fdf4',borderRadius:12,padding:'10px 14px',border:'1px solid #bbf7d0',
                display:'flex',alignItems:'center',gap:8}}>
                <CheckCircle size={15} color="#16a34a"/>
                <div style={{fontSize:12,color:'#15803d',fontWeight:600}}>{m.noteExpiry}</div>
              </div>
              <button onClick={()=>setMode('register')}
                style={{...accentStyle,background:'transparent',color:'#6366f1',border:'2px solid #6366f1'}}>
                <User size={16}/> {m.updateDetails}
              </button>
              <button onClick={()=>{onLogout();onClose();}}
                style={{background:'none',border:'none',color:'#ef4444',fontSize:13,
                  cursor:'pointer',padding:'10px',textAlign:'center',fontWeight:600}}>
                {m.signOut}
              </button>
            </div>
          )}

          {/* ── CHOOSE ── */}
          {mode==='choose'&&(
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div style={{textAlign:'center',padding:'10px 0 20px'}}>
                <div style={{width:72,height:72,borderRadius:'50%',background:'linear-gradient(135deg,#eef2ff,#ede9fe)',
                  display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px'}}>
                  <User size={32} color="#6366f1"/>
                </div>
                <div style={{fontWeight:800,fontSize:18,color:'#0f172a',marginBottom:6}}>Welcome!</div>
                <div style={{fontSize:13,color:'#64748b',lineHeight:1.5}}>
                  Sign in to pre-fill your details and track your orders easily.
                </div>
              </div>
              <button onClick={()=>{setMode('login');setErr('');}} style={accentStyle}>
                <Phone size={16}/> {m.loginTitle}
              </button>
              <button onClick={()=>{setMode('register');setErr('');}}
                style={{...accentStyle,background:'transparent',color:'#6366f1',border:'2px solid #6366f1'}}>
                <User size={16}/> {m.registerTitle}
              </button>
              <button onClick={onClose}
                style={{background:'none',border:'none',color:'#94a3b8',fontSize:13,cursor:'pointer',padding:'8px',textAlign:'center'}}>
                {m.continueGuest}
              </button>
            </div>
          )}

          {/* ── LOGIN ── */}
          {mode==='login'&&(
            <div>
              <div style={{fontSize:13,color:'#64748b',marginBottom:18,lineHeight:1.5}}>
                {m.loginDesc}
              </div>
              <Field icon={<Phone size={13}/>} label={m.phone}>
                <input value={phone} onChange={e=>setPhone(e.target.value)}
                  onKeyDown={e=>e.key==='Enter'&&login()}
                  placeholder="+855 12 345 678" type="tel" style={inputStyle}/>
              </Field>
              <button onClick={login} disabled={busy} style={{...accentStyle,marginBottom:12,opacity:busy?.6:1}}>
                {busy?<Loader size={16} style={{animation:'spin 1s linear infinite'}}/>:<Phone size={16}/>}
                {busy?m.loggingIn:m.loginTitle}
              </button>
              <button onClick={()=>{setMode('register');setErr('');}}
                style={{background:'none',border:'none',color:'#6366f1',fontSize:13,cursor:'pointer',
                  width:'100%',textAlign:'center',padding:'8px',fontWeight:600}}>
                {m.dontHaveAccount}
              </button>
              <button onClick={()=>{setMode('choose');setErr('');}}
                style={{background:'none',border:'none',color:'#94a3b8',fontSize:13,cursor:'pointer',
                  width:'100%',textAlign:'center',padding:'4px'}}>
                ← Back
              </button>
            </div>
          )}

          {/* ── REGISTER / UPDATE ── */}
          {mode==='register'&&(
            <div>
              <div style={{fontSize:13,color:'#64748b',marginBottom:18,lineHeight:1.5}}>
                {customer ? m.updateDetails+':' : m.registerDesc}
              </div>
              <Field icon={<User size={13}/>} label={m.fullName}>
                <input value={name||(customer?.name||'')} onChange={e=>setName(e.target.value)}
                  placeholder="Your name" style={inputStyle}/>
              </Field>
              <Field icon={<Phone size={13}/>} label={m.phone+' *'}>
                <input value={phone||(customer?.phone||'')} onChange={e=>setPhone(e.target.value)}
                  placeholder="+855 12 345 678" type="tel" style={inputStyle}/>
              </Field>
              <Field icon={<MapPin size={13}/>} label={m.address}>
                <textarea value={address||(customer?.address||'')} onChange={e=>setAddress(e.target.value)}
                  placeholder="Street, district, city..." rows={2}
                  style={{...inputStyle,resize:'none',lineHeight:1.5}}/>
              </Field>
              <div style={{fontSize:11,color:'#94a3b8',marginBottom:16,lineHeight:1.5,
                padding:'10px 12px',background:'#f8fafc',borderRadius:10,border:'1px solid #f1f5f9'}}>
                📋 {m.noteExpiry}
              </div>
              <button onClick={register} disabled={busy} style={{...accentStyle,marginBottom:12,opacity:busy?.6:1}}>
                {busy?<Loader size={16} style={{animation:'spin 1s linear infinite'}}/>:<CheckCircle size={16}/>}
                {busy?(customer?m.savingChanges:m.creatingAccount):(customer?m.saveChanges:m.registerTitle)}
              </button>
              {!customer&&(
                <button onClick={()=>{setMode('login');setErr('');}}
                  style={{background:'none',border:'none',color:'#6366f1',fontSize:13,cursor:'pointer',
                    width:'100%',textAlign:'center',padding:'8px',fontWeight:600}}>
                  {m.alreadyHaveAccount}
                </button>
              )}
              <button onClick={()=>{setMode(customer?'profile':'choose');setErr('');}}
                style={{background:'none',border:'none',color:'#94a3b8',fontSize:13,cursor:'pointer',
                  width:'100%',textAlign:'center',padding:'4px'}}>
                ← Back
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════
   MAIN E-MENU PAGE
════════════════════════════════════════════ */
export default function EMenuPage(){
  const mob=useIsMobile();
  const{lang,setLang}=useLang();
  const m=EM[lang]||EM.en;  // translation shorthand
  const[menu,setMenu]=useState(null);
  const[loading,setLoading]=useState(true);
  const[err,setErr]=useState('');
  const[search,setSearch]=useState('');
  const[cat,setCat]=useState('all');
  const[cart,setCart]=useState([]);
  const[showCart,setShowCart]=useState(false);
  const[showTrack,setShowTrack]=useState(false);
  const[showAuth,setShowAuth]=useState(false);
  const[submitting,setSubmitting]=useState(false);
  const catBarRef=useRef(null);

  // Get branch code from URL: /menu/:branchCode
  const branchCode=window.location.pathname.split('/menu/')[1]?.split('/')[0]||'';

  // ── Default to Khmer on e-menu (per-emenu key so it doesn't affect main app) ──
  useEffect(()=>{
    const ememuLangKey=`emenu_lang_${branchCode}`;
    const saved=localStorage.getItem(ememuLangKey);
    if(!saved){
      setLang('km'); // default Khmer for e-menu
      localStorage.setItem(ememuLangKey,'km');
    } else {
      setLang(saved);
    }
  },[branchCode]);

  // ── Customer: load from localStorage on mount ──
  const[customer,setCustomer]=useState(()=>loadSavedCustomer(branchCode));

  const handleAuthSuccess=(cust)=>{
    setCustomer(cust);
    setShowAuth(false);
  };
  const handleLogout=()=>{
    clearCustomer(branchCode);
    setCustomer(null);
  };

  const load=useCallback(async()=>{
    if(!branchCode){setErr('Invalid menu link');setLoading(false);return}
    try{
      const r=await axios.get(`${API}/api/public/menu/${branchCode}`);
      setMenu(r.data);
      // Update page title dynamically per branch
      if(r.data?.branch?.name){
        document.title=`${r.data.branch.name} — Digital Menu`;
      }
    }catch(e){setErr(e.response?.data?.error||'Menu unavailable')}
    finally{setLoading(false)}
  },[branchCode]);

  // Reset title when leaving e-menu page
  useEffect(()=>{
    return()=>{ document.title='ERPhub'; };
  },[]);

  useEffect(()=>{load()},[load]);

  /* ── filtered products ── */
  const products=(menu?.products||[]).filter(p=>{
    const q=search.toLowerCase();
    const ms=!search||p.name.toLowerCase().includes(q)||(p.name_kh||'').toLowerCase().includes(q)||(p.category?.name||'').toLowerCase().includes(q);
    const mc=cat==='all'||String(p.category?.id)===cat;
    return ms&&mc;
  }).sort((a,b)=>{
    // Featured first, then by popularity desc, then name
    if(a.emenu_featured&&!b.emenu_featured) return -1;
    if(!a.emenu_featured&&b.emenu_featured) return 1;
    const ap=a.emenu_popularity||0, bp=b.emenu_popularity||0;
    if(bp!==ap) return bp-ap;
    return (a.name||'').localeCompare(b.name||'');
  });

  const categories=(menu?.categories||[]).filter(c=>{
    return (menu?.products||[]).some(p=>String(p.category?.id)===String(c.id));
  });

  /* ── cart helpers ── */
  const cartKey=(productId,uomId)=>`${productId}_${uomId||'base'}`;

  const addToCart=(p,uomId,price)=>{
    const uomList=(p.uoms||[]).filter(u=>parseFloat(u.price)>0);
    const uomName=uomId?uomList.find(u=>u.uom_id===uomId)?.uom?.name:p.uom?.name||'Unit';
    const key=cartKey(p.id,uomId);
    const cartName=(lang==='km'&&p.name_kh)?p.name_kh:p.name;
    setCart(c=>{
      const idx=c.findIndex(i=>i.key===key);
      if(idx>=0){const n=[...c];n[idx]={...n[idx],qty:n[idx].qty+1};return n}
      return[...c,{key,product_id:p.id,uom_id:uomId||null,name:cartName,uomName,price,image_url:p.image_url,qty:1}];
    });
  };

  const decCart=(productId,uomId)=>{
    const key=cartKey(productId,uomId);
    setCart(c=>{
      const idx=c.findIndex(i=>i.key===key);
      if(idx<0)return c;
      const n=[...c];
      if(n[idx].qty<=1)return n.filter(i=>i.key!==key);
      n[idx]={...n[idx],qty:n[idx].qty-1};return n;
    });
  };

  const setQty=(idx,qty)=>{if(qty<=0){setCart(c=>c.filter((_,i)=>i!==idx));return}setCart(c=>c.map((i,j)=>j===idx?{...i,qty}:i))};
  const removeIdx=idx=>setCart(c=>c.filter((_,i)=>i!==idx));

  const getCartItemsForProduct=(productId)=>{
    return cart.filter(i=>i.product_id===productId);
  };

  const cartCount=cart.reduce((s,i)=>s+i.qty,0);
  const cartTotal=cart.reduce((s,i)=>s+i.price*i.qty,0);

  /* ── submit ── */
  const submitOrder=async(info)=>{
    setSubmitting(true);
    try{
      const r=await axios.post(`${API}/api/public/menu/${branchCode}/order`,{
        customer_id: customer?.id || null,
        customer_name:info.name.trim(),
        customer_phone:info.phone.trim()||null,
        customer_address:info.address.trim()||null,
        telegram_username:info.telegram.replace('@','').trim()||null,
        notes:info.notes.trim()||null,
        items:cart.map(i=>({product_id:i.product_id,quantity:i.qty,uom_id:i.uom_id})),
      });
      setCart([]);
      return r.data;
    }catch(e){
      alert(e.response?.data?.error||'Failed to place order. Please try again.');
      return null;
    }finally{setSubmitting(false)}
  };

  /* ── LOADING ── */
  if(loading)return(
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f8fafc'}}>
      <div style={{textAlign:'center'}}>
        <div style={{width:64,height:64,borderRadius:'50%',background:'linear-gradient(135deg,#6366f1,#4f46e5)',
          display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px',
          animation:'pulse 1.5s ease infinite'}}>
          <Package size={28} color="#fff"/>
        </div>
        <div style={{color:'#64748b',fontSize:14,fontWeight:600}}>Loading menu...</div>
      </div>
    </div>
  );

  if(err)return(
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f8fafc',padding:24}}>
      <div style={{textAlign:'center',maxWidth:320}}>
        <div style={{fontSize:52,marginBottom:16}}>😔</div>
        <h2 style={{color:'#0f172a',marginBottom:8,fontSize:20}}>Menu Unavailable</h2>
        <p style={{color:'#64748b',fontSize:14,lineHeight:1.6}}>{err}</p>
      </div>
    </div>
  );

  const{branch}=menu;

  return(
    <div style={{minHeight:'100vh',background:'#f8fafc',fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',overflowX:'hidden'}}>
      <style>{`
        *{box-sizing:border-box;-webkit-tap-highlight-color:transparent}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes pulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.05);opacity:.8}}
        @keyframes slideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}
        @keyframes cartPop{0%{transform:scale(.8)}60%{transform:scale(1.15)}100%{transform:scale(1)}}
        input,textarea,select{font-family:inherit}
        ::-webkit-scrollbar{width:3px;height:3px}
        ::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:4px}
      `}</style>

      {/* ═══ HERO HEADER ═══ */}
      <div style={{
        background:'linear-gradient(160deg,#4f46e5 0%,#6366f1 40%,#818cf8 100%)',
        padding:mob?'14px 16px 80px':'28px 24px 90px',
        position:'relative',overflow:'hidden',
      }}>
        {/* bg deco */}
        {[{top:-60,right:-60,s:240},{bottom:-80,left:-40,s:200},{top:'40%',right:'15%',s:120}].map((d,i)=>(
          <div key={i} style={{position:'absolute',top:d.top,right:d.right,bottom:d.bottom,left:d.left,
            width:d.s,height:d.s,borderRadius:'50%',background:'rgba(255,255,255,.06)'}}/>
        ))}

        <div style={{position:'relative',zIndex:1,maxWidth:560,margin:'0 auto'}}>
          {/* ── Top bar: lang toggle + account + track ── */}
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
            <div style={{display:'flex',borderRadius:20,overflow:'hidden',border:'1px solid rgba(255,255,255,.3)'}}>
              {['km','en'].map(code=>(
                <button key={code} onClick={()=>{
                  setLang(code);
                  localStorage.setItem(`emenu_lang_${branchCode}`,code);
                }} style={{
                  padding:'5px 12px',border:'none',cursor:'pointer',fontSize:12,fontWeight:700,
                  background:lang===code?'rgba(255,255,255,.9)':'transparent',
                  color:lang===code?'#4f46e5':'rgba(255,255,255,.8)',
                  transition:'all .15s',
                }}>{code==='km'?'ខ្មែរ':'EN'}</button>
              ))}
            </div>
            <div style={{display:'flex',gap:7,alignItems:'center'}}>
              {customer?(
                <button onClick={()=>setShowAuth(true)}
                  style={{background:'rgba(255,255,255,.18)',border:'1px solid rgba(255,255,255,.3)',
                    borderRadius:20,padding:'6px 12px',color:'#fff',cursor:'pointer',
                    fontSize:12,fontWeight:700,display:'flex',alignItems:'center',gap:5,maxWidth:100}}>
                  <User size={13}/><span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{customer.name.split(' ')[0]}</span>
                </button>
              ):(
                <button onClick={()=>setShowAuth(true)}
                  style={{background:'rgba(255,255,255,.15)',border:'1px solid rgba(255,255,255,.25)',
                    borderRadius:20,padding:'6px 12px',color:'#fff',cursor:'pointer',
                    fontSize:12,fontWeight:700,display:'flex',alignItems:'center',gap:5}}>
                  <User size={13}/>{m.loginBtn}
                </button>
              )}
              <button onClick={()=>setShowTrack(true)}
                style={{background:'rgba(255,255,255,.15)',border:'1px solid rgba(255,255,255,.25)',
                  borderRadius:20,padding:'6px 12px',color:'#fff',cursor:'pointer',
                  fontSize:12,fontWeight:700,display:'flex',alignItems:'center',gap:5}}>
                <ClipboardList size={13}/>{m.trackBtn}
              </button>
            </div>
          </div>

          {/* ── Branch identity ── */}
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div style={{width:mob?44:50,height:mob?44:50,borderRadius:14,overflow:'hidden',flexShrink:0,
              border:'2px solid rgba(255,255,255,.3)',
              background:'rgba(255,255,255,.15)',display:'flex',alignItems:'center',justifyContent:'center'}}>
              {branch.logo_url
                ?<Img src={branch.logo_url} alt={branch.name} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                :<Package size={20} color="rgba(255,255,255,.9)"/>}
            </div>
            <div>
              <div style={{color:'rgba(255,255,255,.7)',fontSize:9,fontWeight:700,letterSpacing:1,textTransform:'uppercase'}}>DIGITAL MENU</div>
              <div style={{color:'#fff',fontSize:mob?16:20,fontWeight:900,lineHeight:1.2}}>{branch.name}</div>
            </div>
          </div>

          {/* branch info pills */}
          {(branch.city||branch.phone)&&(
            <div style={{display:'flex',gap:12,marginTop:10,flexWrap:'wrap'}}>
              {branch.city&&<span style={{fontSize:11,color:'rgba(255,255,255,.8)',display:'flex',alignItems:'center',gap:4}}><MapPin size={10}/>{branch.city}</span>}
              {branch.phone&&<span style={{fontSize:11,color:'rgba(255,255,255,.8)',display:'flex',alignItems:'center',gap:4}}><Phone size={10}/>{branch.phone}</span>}
            </div>
          )}
        </div>
      </div>

      {/* ═══ STICKY SEARCH + CATEGORIES ═══ */}
      <div style={{
        position:'sticky',top:0,zIndex:30,
        background:'#fff',
        borderRadius:'22px 22px 0 0',
        marginTop:-28,
        boxShadow:'0 4px 24px rgba(0,0,0,.08)',
      }}>
        <div style={{padding:mob?'14px 14px 0':'16px 20px 0',maxWidth:560,margin:'0 auto'}}>
          {/* search */}
          <div style={{position:'relative',marginBottom:10}}>
            <Search size={15} style={{position:'absolute',left:13,top:'50%',transform:'translateY(-50%)',color:'#94a3b8'}}/>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder={m.searchPlaceholder}
              style={{...inputStyle,paddingLeft:38,marginBottom:0,background:'#f1f5f9'}}/>
            {search&&<button onClick={()=>setSearch('')}
              style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#94a3b8',display:'flex',alignItems:'center'}}>
              <X size={14}/>
            </button>}
          </div>
        </div>
        {/* category scroll */}
        <div ref={catBarRef} style={{
          padding:'0 14px 12px',maxWidth:560,margin:'0 auto',
          display:'flex',gap:7,overflowX:'auto',
          scrollbarWidth:'none',WebkitScrollbarWidth:'none',
        }}>
          {[{id:'all',name:'All 🍽️'},...categories].map(c=>(
            <button key={c.id} onClick={()=>setCat(String(c.id))}
              style={{flexShrink:0,padding:'7px 16px',borderRadius:22,border:'none',cursor:'pointer',
                fontSize:12,fontWeight:700,transition:'all .2s',whiteSpace:'nowrap',
                background:cat===String(c.id)?'#6366f1':'#f1f5f9',
                color:cat===String(c.id)?'#fff':'#475569',
                boxShadow:cat===String(c.id)?'0 4px 14px rgba(99,102,241,.38)':'none'}}>
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* ═══ PRODUCTS ═══ */}
      <div style={{maxWidth:560,margin:'0 auto',padding:mob?'12px 14px 140px':'16px 20px 140px'}}>
        {products.length===0?(
          <div style={{textAlign:'center',padding:'60px 20px',animation:'slideUp .4s ease'}}>
            <Package size={48} color="#cbd5e1" style={{marginBottom:12}}/>
            <div style={{color:'#94a3b8',fontSize:15,fontWeight:600}}>No products found</div>
            {search&&<button onClick={()=>setSearch('')} style={{marginTop:10,color:'#6366f1',background:'none',border:'none',cursor:'pointer',fontSize:13,fontWeight:700}}>Clear search</button>}
          </div>
        ):(
          <div style={{
            display:'grid',
            gridTemplateColumns:mob?'repeat(2,1fr)':'repeat(3,1fr)',
            gap:mob?10:12,
          }}>
            {products.map(p=>(
              <ProductCard key={`${p.id}-${lang}`} p={p} mob={mob} lang={lang}
                cartItem={getCartItemsForProduct(p.id)}
                onAdd={addToCart}
                onDec={decCart}/>
            ))}
          </div>
        )}

        {/* footer */}
        <div style={{textAlign:'center',marginTop:36,paddingTop:20,borderTop:'1px solid #e2e8f0'}}>
          <div style={{fontSize:10,color:'#cbd5e1',fontWeight:600,letterSpacing:.5}}>POWERED BY ERPHUB</div>
        </div>
      </div>

      {/* ═══ FLOATING CART BUTTON ═══ */}
      {cartCount>0&&(
        <button onClick={()=>setShowCart(true)} style={{
          position:'fixed',bottom:24,right:mob?16:24,zIndex:100,
          background:'linear-gradient(135deg,#6366f1,#4f46e5)',
          color:'#fff',border:'none',
          borderRadius:30,padding:mob?'12px 18px':'14px 22px',
          display:'flex',alignItems:'center',gap:10,
          boxShadow:'0 8px 32px rgba(99,102,241,.5)',
          cursor:'pointer',fontSize:mob?14:15,fontWeight:800,
          animation:'cartPop .3s ease',
          transition:'transform .15s,box-shadow .15s',
        }}>
          <div style={{position:'relative'}}>
            <ShoppingCart size={mob?20:22}/>
            <div style={{
              position:'absolute',top:-9,right:-9,
              width:19,height:19,borderRadius:'50%',
              background:'#ef4444',fontSize:10,fontWeight:900,
              display:'flex',alignItems:'center',justifyContent:'center',
              border:'2px solid #fff'
            }}>{cartCount>99?'99+':cartCount}</div>
          </div>
          <span>{fmt(cartTotal)}</span>
          <ChevronRight size={16}/>
        </button>
      )}

      {/* ═══ DRAWERS ═══ */}
      {showCart&&(
        <CartSheet
          cart={cart} branch={branch}
          customer={customer}
          onClose={()=>setShowCart(false)}
          onQty={setQty}
          onRemove={removeIdx}
          onSubmit={submitOrder}
          submitting={submitting}
          lang={lang}/>
      )}
      {showTrack&&<TrackDrawer onClose={()=>setShowTrack(false)} lang={lang}/>}
      {showAuth&&(
        <CustomerAuthModal
          branchCode={branchCode}
          onClose={()=>setShowAuth(false)}
          onSuccess={handleAuthSuccess}
          customer={customer}
          onLogout={handleLogout}
          lang={lang}/>
      )}
    </div>
  );
}
